import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Calendar } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api, getMl, fileUrl } from "@/lib/api";

const NewsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
    api.get("/news").then((r) => setRelated(r.data.filter((n) => n.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (loading) {
    return <div className="container-nakia py-32 text-center text-slate-500" data-testid="news-detail-loading">{t("common.loading")}</div>;
  }
  if (!article) {
    return (
      <div className="container-nakia py-32 text-center" data-testid="news-detail-not-found">
        <p className="text-slate-500 mb-6">{t("common.noResults")}</p>
        <Link to="/news" className="btn-outline-navy"><ArrowLeft size={14} /> {t("common.backToList")}</Link>
      </div>
    );
  }

  const titleLabels = { en: "News & Insights", tr: "Haberler & Görüşler", id: "Berita & Wawasan" };
  const fullArticleLabel = { en: "Read Full Article", tr: "Tam Makaleyi Oku", id: "Baca Artikel Lengkap" }[lang];
  const externalSourceLabel = { en: "External Source", tr: "Dış Kaynak", id: "Sumber Eksternal" }[lang];

  const body = getMl(article.body, lang) || getMl(article.excerpt, lang);

  return (
    <div data-testid="news-detail-page" className="bg-white">
      {/* Hero */}
      <section className="bg-[#0A1128] text-white py-20 md:py-28 relative overflow-hidden">
        {article.cover_image && (
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(10,17,40,0.85) 0%, rgba(10,17,40,0.95) 100%), url('${fileUrl(article.cover_image)}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}
        <div className="container-nakia relative max-w-4xl">
          <button
            onClick={() => navigate("/news")}
            data-testid="news-detail-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#D4AF37] mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> {titleLabels[lang]}
          </button>
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-divider" />
            <span className="text-eyebrow text-[#D4AF37] flex items-center gap-2">
              <Calendar size={12} />
              {new Date(article.published_at).toLocaleDateString(lang, { year: "numeric", month: "long", day: "numeric" })}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.1] max-w-3xl">
            {getMl(article.title, lang)}
          </h1>
          {getMl(article.excerpt, lang) && (
            <p className="text-white/80 text-lg leading-relaxed mt-7 max-w-2xl">
              {getMl(article.excerpt, lang)}
            </p>
          )}
        </div>
      </section>

      {/* Cover image (between hero and body for visual hierarchy) */}
      {article.cover_image && (
        <section className="bg-white">
          <div className="container-nakia max-w-4xl -mt-12 relative z-10">
            <div className="aspect-[16/9] overflow-hidden rounded-sm shadow-2xl border border-white/5">
              <img src={fileUrl(article.cover_image)} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="py-16 md:py-24 bg-white">
        <article className="container-nakia max-w-3xl">
          <div
            className="prose-nakia text-[#0A1128]/90 text-base sm:text-lg leading-[1.85] whitespace-pre-line"
            data-testid="news-detail-body"
          >
            {body || <em className="text-slate-400">No content yet.</em>}
          </div>

          {/* External link CTA */}
          {article.external_link && (
            <div className="mt-14 pt-10 border-t border-slate-200" data-testid="news-detail-external-cta">
              <div className="text-eyebrow text-[#D4AF37] mb-4">{externalSourceLabel}</div>
              <div className="bg-[#0A1128] rounded-sm p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div className="text-white/85 max-w-xl">
                  <p className="font-display text-xl mb-1.5">{fullArticleLabel}</p>
                  <p className="text-xs text-white/55 break-all">{article.external_link}</p>
                </div>
                <a
                  href={article.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="news-detail-external-link"
                  className="btn-gold whitespace-nowrap"
                >
                  {fullArticleLabel} <ExternalLink size={14} />
                </a>
              </div>
            </div>
          )}
        </article>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F8FAFC] border-t border-slate-200">
          <div className="container-nakia">
            <div className="flex items-center gap-3 mb-8">
              <span className="gold-divider" />
              <span className="text-eyebrow text-[#D4AF37]">More Insights</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.slug}`}
                  data-testid={`related-news-${n.slug}`}
                  className="group card-light overflow-hidden flex flex-col"
                >
                  {n.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={fileUrl(n.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
                      {new Date(n.published_at).toLocaleDateString(lang)}
                    </div>
                    <h3 className="font-display text-lg text-[#0A1128] group-hover:text-[#D4AF37] transition-colors">
                      {getMl(n.title, lang)}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsDetail;
